import { motion } from "framer-motion";

const STAGES = [
  { key: "Clarity", tag: "01" },
  { key: "Alignment", tag: "02" },
  { key: "Execution", tag: "03" },
  { key: "Value Realisation", tag: "04" },
  { key: "Choice", tag: "05" },
];

/** Circular five-stage framework — signature visual */
export function CircularFramework({ size = 520, activeIndex = null }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.36;
  const nodeR = 62;

  const points = STAGES.map((_, i) => {
    const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / STAGES.length;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle), angle };
  });

  return (
    <div className="relative w-full max-w-[560px] aspect-square mx-auto">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#E86A20" />
          </marker>
        </defs>
        {/* dashed circle */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0E2148" strokeOpacity="0.18" strokeDasharray="2 6" />

        {/* Arc arrows between nodes */}
        {points.map((p, i) => {
          const next = points[(i + 1) % points.length];
          // shift start/end away from node edges
          const dx = next.x - p.x;
          const dy = next.y - p.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          const ux = dx / len;
          const uy = dy / len;
          const sx = p.x + ux * (nodeR + 4);
          const sy = p.y + uy * (nodeR + 4);
          const ex = next.x - ux * (nodeR + 10);
          const ey = next.y - uy * (nodeR + 10);
          // Curve outward
          const midX = (sx + ex) / 2 + (uy) * 40;
          const midY = (sy + ey) / 2 - (ux) * 40;
          return (
            <path
              key={i}
              d={`M${sx},${sy} Q${midX},${midY} ${ex},${ey}`}
              fill="none"
              stroke="#E86A20"
              strokeOpacity="0.85"
              strokeWidth="1.4"
              markerEnd="url(#arrow)"
            />
          );
        })}

        {/* Nodes */}
        {points.map((p, i) => {
          const s = STAGES[i];
          const isActive = activeIndex === i;
          return (
            <g key={s.key}>
              <circle
                cx={p.x}
                cy={p.y}
                r={nodeR}
                fill={isActive ? "#0E2148" : "#FFFFFF"}
                stroke="#0E2148"
                strokeWidth={isActive ? 0 : 1.2}
              />
              <text
                x={p.x}
                y={p.y - 6}
                textAnchor="middle"
                fontFamily="IBM Plex Mono, monospace"
                fontSize="10"
                letterSpacing="0.18em"
                fill={isActive ? "#FAF8F4" : "#E86A20"}
              >
                {s.tag}
              </text>
              <text
                x={p.x}
                y={p.y + 14}
                textAnchor="middle"
                fontFamily="Cormorant Garamond, serif"
                fontSize={s.key.length > 10 ? 15 : 18}
                fill={isActive ? "#FAF8F4" : "#0E2148"}
              >
                {s.key}
              </text>
            </g>
          );
        })}

        {/* Center label */}
        <text x={cx} y={cy - 6} textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="24" fill="#0E2148">
          renewal
        </text>
        <text x={cx} y={cy + 18} textAnchor="middle" className="tick-label" fill="#E86A20">
          the cycle
        </text>
      </svg>
    </div>
  );
}

/** Linear stack: Clarity → Alignment → … → Choice */
export function LinearFramework() {
  return (
    <div className="flex flex-col items-start gap-2">
      {STAGES.map((s, i) => (
        <motion.div
          key={s.key}
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.08 }}
          className="w-full"
        >
          <div className="flex items-baseline gap-6">
            <span className="tick-label w-8">{s.tag}</span>
            <span className="display-serif text-3xl md:text-5xl">{s.key}</span>
          </div>
          {i < STAGES.length - 1 && (
            <div className="pl-16 py-2 text-orange text-2xl leading-none">↓</div>
          )}
        </motion.div>
      ))}
    </div>
  );
}
